import type { FC } from 'react';
import { useColourPalette } from 'src/hooks/useColourPalette';

import type { LineChartSeries } from '../../../../components/highcharts/line-chart/useLineChartOptions';
import { useLineChartOptions } from '../../../../components/highcharts/line-chart/useLineChartOptions';
import { HighchartsWidget } from '../../HighchartsWidget';
import { NoWidgetData } from '../NoWidgetData';
import { WidgetLoading } from '../widget-loading';

export type Props = {
  isLoading?: boolean;
  xTitle: string;
  yTitle: string;
  values?: { x: Date; y: number }[];
  series?: LineChartSeries[];
  tooltipYValueFormatter?: (
    y: number | undefined,
    seriesIndex: number,
    custom?: Record<string, unknown>
  ) => string;
  pointColorFormatter?: (
    y: number | undefined,
    seriesIndex: number,
    custom?: Record<string, unknown>
  ) => string | undefined;
  /**
   * Flat horizontal reference lines (e.g. indicator tolerances/appetites) drawn
   * as Highcharts `yAxis.plotLines`. When present, the y-axis is extended so the
   * lines stay in view.
   */
  referenceLines?: { value: number; color: string }[];
};

const LineChartWidget: FC<Props> = ({
  values,
  series,
  isLoading,
  xTitle,
  yTitle,
  tooltipYValueFormatter,
  pointColorFormatter,
  referenceLines,
}) => {
  const { colours } = useColourPalette();

  const options = useLineChartOptions({
    xTitle,
    yTitle,
    colours,
    values,
    series,
    tooltipYValueFormatter,
    pointColorFormatter,
    referenceLines,
  });

  if (isLoading) {
    return <WidgetLoading />;
  }

  if ((!values || values.length === 0) && (!series || series.length === 0)) {
    return <NoWidgetData />;
  }

  return <HighchartsWidget options={options} />;
};

export default LineChartWidget;
